import React, { useState } from "react";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";

interface VehiclesTableToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedCount: number;
  onDeleteSelected: () => Promise<void>;
  onAddRow?: () => void;
  disabled?: boolean;
  isDeleting?: boolean;
}

export const VehiclesTableToolbar: React.FC<VehiclesTableToolbarProps> = ({
  searchValue,
  onSearchChange,
  selectedCount,
  onDeleteSelected,
  onAddRow,
  disabled,
  isDeleting,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteClick = () => setConfirmOpen(true);
  const handleConfirm = async () => {
    setDeleteError(null);
    try {
      await onDeleteSelected();
      setConfirmOpen(false);
    } catch {
      setDeleteError("Failed to delete vehicles. Please try again.");
    }
  };
  const handleCancel = () => {
    setConfirmOpen(false);
    setDeleteError(null);
  };

  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ paddingBottom: 1.5 }}>
        {onAddRow && (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onAddRow} disabled={disabled}>
            Add Vehicle
          </Button>
        )}

        <TextField
          size="small"
          placeholder="Search by plate, make, model, state…"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          disabled={disabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280, flexGrow: 1 }}
        />

        {selectedCount > 0 && (
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={handleDeleteClick}
            disabled={disabled}
          >
            Delete selected ({selectedCount})
          </Button>
        )}
      </Stack>

      <Dialog open={confirmOpen} onClose={isDeleting ? undefined : handleCancel}>
        <DialogTitle>
          Delete {selectedCount} vehicle{selectedCount !== 1 ? "s" : ""}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>These vehicles will be removed from your account.</DialogContentText>
          {deleteError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehiclesTableToolbar;
